// Tech Stuff — full prompt & LLM usage catalogue shown on /documentation
// Kept in sync with:
//   /app/backend/prompts.py              (31 section prompts + master system)
//   /app/backend/server.py               (inline prompts: audio, references, executive brief, illustration, infographic)
//   /app/backend/routes/illustration_html.py (Claude HTML infographic prompt)

export const LLM_MODELS = [
  {
    name: 'Claude Sonnet 4.5',
    provider: 'Anthropic (via Emergent Universal Key)',
    id: 'claude-sonnet-4-5-20250929',
    used_for: '31 section generation (Part A + Part B), Executive Investment Brief (PDF), References compilation, Audio narration script, Video scene prompts',
    pricing: '$5 input / $25 output per 1M tokens',
    streaming: 'Yes — chunked streaming to stay under the 60s proxy ceiling',
  },
  {
    name: 'Claude Sonnet 4.6',
    provider: 'Anthropic (via Emergent Universal Key)',
    id: 'claude-sonnet-4-6',
    used_for: 'Illustration fallback: generates self-contained HTML + inline SVG that is rendered to PNG via headless Chromium (Playwright). Used when Gemini image generation is unavailable.',
    pricing: '$5 input / $25 output per 1M tokens',
    streaming: 'Non-streaming, 200s outer timeout',
  },
  {
    name: 'Gemini Nano Banana',
    provider: 'Google (via Emergent Universal Key)',
    id: 'gemini-3.1-flash-image-preview',
    used_for: 'Primary illustration generator: per-section infographics + full-page investment infographic. Multimodal response (text + image).',
    pricing: '$0.04 per image',
    streaming: 'Non-streaming, 90s outer timeout per attempt',
  },
  {
    name: 'OpenAI TTS',
    provider: 'OpenAI (via Emergent Universal Key)',
    id: 'tts-1-hd (voice: onyx, speed: 0.95)',
    used_for: 'Audio Overview — 2-4 min MP3 narration of the executive brief. Also used for Sora 2 cinematic brief voiceover (UI currently disabled).',
    pricing: '$15 per 1M characters',
    streaming: 'Non-streaming',
  },
  {
    name: 'Sora 2',
    provider: 'OpenAI (via Emergent Universal Key)',
    id: 'sora-2',
    used_for: 'Cinematic Brief — multi-scene 8s documentary clips stitched by FFmpeg with TTS voiceover overlay. Currently UI-disabled (planned for next release).',
    pricing: '~$0.50 per second of rendered video',
    streaming: 'Asynchronous job',
  },
];

// The master system message applied to all 31 section generations.
export const MASTER_SYSTEM_PROMPT = `You are a senior tier-one strategy economist and country-sector strategist with 20+ years of advisory experience to Fortune 500 corporations, sovereign governments, development finance institutions (IFC, AfDB, World Bank, FMO, DEG, USDFC, BII), and institutional investors across emerging and frontier markets. You write with the analytical rigor, intellectual honesty, and decision-oriented clarity that characterizes top-tier management consulting.

CONTEXT VARIABLES:
- {COUNTRY}: The target country for analysis
- {SECTOR}: The target sector for analysis

MANDATORY STANDARDS FOR EVERY SECTION:
1. DATA RECENCY: Prioritize data from the last 24 months. Flag any data older than 3 years as "legacy benchmark." Always state the reference year alongside every figure.
2. SOURCE RIGOR: Cite only authoritative sources — multilateral institutions (World Bank, IMF, UN, OECD, WTO, UNCTAD), rating agencies (S&P, Moody's, Fitch), national central banks and statistics offices, peer-reviewed journals, top-tier consultancies, and regulated financial disclosures.
3. QUANTIFICATION: Every qualitative assertion must be accompanied by a quantitative anchor (a figure, a ratio, a percentile, a ranking). Avoid vague qualifiers like "significant," "substantial," or "considerable" without numbers.
4. TRIANGULATION: When data sources conflict, present the range and name the sources. Do not average; disclose the divergence.
5. IMPLICATIONS-FIRST WRITING: Every analytical block ends with a "So what?" line that converts the finding into a decision-relevant implication.
6. NO HALLUCINATION: If data is unavailable or unreliable, say so explicitly and offer the nearest reliable proxy. Never fabricate figures.
7. STRUCTURED OUTPUT: Use the exact subsection headers specified in the section prompt. Include the data tables, matrices, and frameworks requested.
8. TONE: Tier-one consulting senior partner — analytical, confident, occasionally contrarian where evidence supports, never promotional. Assume the reader is a CEO, IC member, or sovereign minister with 10 minutes to make a decision.

OUTPUT FORMAT:
- Markdown-ready with H2/H3 headers
- Tables in pipe-separated markdown format
- Bullet points only where they add scanability; otherwise prose
- Every figure cited inline with source and year
- End every section with a standalone "KEY TAKEAWAYS FOR DECISION-MAKERS" box containing 3-5 bullets`;

export const CHUNK_PROMPTS = {
  partA: `{SECTION_PROMPT}

CRITICAL: You are writing PART 1 OF 2 of this section. Produce the FIRST HALF only — cover the opening subsections in depth (context, landscape, quantitative baseline, first frameworks/tables). Keep the response under 800 words. DO NOT write a KEY TAKEAWAYS box yet. Stop cleanly at a natural subsection boundary so Part 2 can continue.

IMPORTANT: Do NOT include any 'Part 1 of 2' header, 'Part 2 continues' footer, or other chunk-marker text in your output — write as if this is one continuous document.`,
  partB: `{SECTION_PROMPT}

CRITICAL: You are writing PART 2 OF 2 of this section, CONTINUING from Part 1. Do NOT repeat anything already covered.

STRICT OUTPUT STRUCTURE — you MUST produce in this exact order:
1) The SECOND HALF continuation covering remaining subsections (deeper frameworks, strategic implications, recommendations) — at most 500 words.
2) Then IMMEDIATELY a '## KEY TAKEAWAYS FOR DECISION-MAKERS' section containing 3-5 bullet points (decision-ready, quantified where possible) — at most 200 words.
3) Finally, on a new line at the very end, print this exact sentinel marker verbatim: \`--- END OF SECTION ---\`. This marker is REQUIRED — do not summarise, omit, or paraphrase it. Nothing may come after it.

Total Part 2 length MUST be under 700 words. Reserve room to complete the KEY TAKEAWAYS box AND the END OF SECTION marker — both are non-negotiable. Be concise; density over breadth.

IMPORTANT: Do NOT include any 'Part 2 of 2', 'PART 2:', 'continuing from Part 1' header or similar chunk-marker text. Write as a seamless continuation.

=== TAIL OF PART 1 FOR CONTINUITY ===
{part_a_tail}
=== END ===

Now write PART 2:`,
  selfHealCompletion: `You previously wrote PART 2 of a section but the output was cut off before the mandatory '--- END OF SECTION ---' marker.

=== TAIL OF YOUR PREVIOUS OUTPUT ===
{tail_b}
=== END ===

Continue from exactly where you stopped. Complete the current sentence/bullet, then finish the KEY TAKEAWAYS FOR DECISION-MAKERS box (add any missing bullets to reach 3-5 total), and finally print the sentinel marker verbatim on its own line: \`--- END OF SECTION ---\`. Keep your continuation under 250 words. Nothing may come after the marker.`,
};

// Full prompt text (verbatim) from /app/backend/prompts.py SECTIONS dict
export const SECTION_PROMPTS = [
  { num: 1, tier: 'SCOUT', key: 'country_snapshot', title: 'Country Snapshot', focus: 'Macro fundamentals, demographics, sovereign ratings, ease-of-doing-business baseline.',
    prompt: `Generate a comprehensive Country Snapshot for {COUNTRY} with specific relevance to the {SECTOR} sector. The output must enable a decision-maker to form a 90-second mental model of the country's fundamentals.

REQUIRED SUBSECTIONS:
1. Geography & Strategic Position — Land area, borders, regional bloc memberships, strategic corridors, climate zones relevant to {SECTOR}.
2. Demographics — Population (total, urban %, median age, growth rate), dependency ratio, labor force participation, literacy, {SECTOR}-relevant skill indicators. Source: UN DESA, national statistics office.
3. Macroeconomic Fundamentals — Nominal GDP (USD), GDP per capita (nominal and PPP), real GDP growth (last 5 years + IMF forecast), inflation (CPI), unemployment, current account balance, fiscal balance, gross debt/GDP, FX reserves (months of imports). Source: IMF WEO latest vintage, World Bank WDI.
4. Human Development — HDI score and global rank, Gini coefficient, poverty headcount ratio. Source: UNDP HDR latest edition.
5. Sovereign Credit Ratings — Current ratings and outlooks from S&P, Moody's, Fitch; include latest rating action date and rationale. If unrated, note and explain.
6. Ease of Doing Business & Competitiveness — World Bank B-READY, WEF Global Competitiveness Index, Corruption Perceptions Index rank, Economic Freedom Index. Explicitly name the year of each score.
7. {SECTOR}-Specific Country Profile — 3-4 paragraph narrative on the country's positioning, track record, and structural features in {SECTOR}.

REQUIRED DELIVERABLES:
- One-glance indicators table (20-row markdown table with indicator | value | year | source).
- 5-year macro time-series table (GDP growth, inflation, fiscal balance, current account, debt/GDP).
- Sovereign ratings summary (3 agencies x [rating, outlook, last action, date]).

LENGTH: 900-1,100 words plus tables.
DATA CUTOFF: Flag any data older than 36 months.
CLOSING: KEY TAKEAWAYS FOR DECISION-MAKERS box with 3-5 bullets answering "What do I need to remember about {COUNTRY} for {SECTOR}?"` },

  { num: 2, tier: 'SCOUT', key: 'executive_summary', title: 'Executive Summary', focus: 'Senior-partner one-page synthesis: opportunity, risks, recommendation.',
    prompt: `Write an Executive Summary for the {COUNTRY} - {SECTOR} opportunity. This is the single most important section - it will be read by CEOs, IC members, and sovereign ministers who will make go/no-go decisions based on it.

WRITING STYLE: Tier-one consulting senior partner writing to a CEO. Every sentence must earn its place. No fluff, no throat-clearing. Lead with the answer.

REQUIRED STRUCTURE:
1. The Headline (2-3 sentences): The single most important finding about the {COUNTRY} - {SECTOR} opportunity - framed as an insight, not a fact.
2. Opportunity Framing (80-100 words): What is the market opportunity? Size it in USD. Anchor it against a comparable reference market.
3. The 3 "So-What" Insights - Three numbered insights, each a standalone paragraph of 60-80 words with: Insight headline (bold, under 12 words), Evidence (1-2 quantified data points), Strategic implication (the "therefore..." statement).
4. Critical Risks (3-4 bullets) - The risks that could derail the thesis, each with a severity rating (HIGH/MEDIUM/LOW) and a mitigation pointer.
5. Strategic Recommendation - One of: PURSUE NOW / PURSUE WITH GUARDRAILS / WATCH / PASS. Justify in 2-3 sentences.
6. Deal Archetype - Suggest the optimal entry vehicle: Greenfield / JV / M&A / PPP / Concession / Off-take / Licensing. One-sentence rationale.
7. Sector Status Line - One sentence classifying the {SECTOR} in {COUNTRY} as: Nascent / Emerging / Maturing / Mature / Saturated / Declining - with evidence.

REQUIRED DELIVERABLES:
- Opportunity-at-a-glance box: Market size, 5-yr CAGR, IRR band, payback period, recommended entry vehicle, top risk.
- Visual cue: Recommendation ribbon (PURSUE NOW / PURSUE WITH GUARDRAILS / WATCH / PASS).

LENGTH: 550-700 words. Ruthless brevity.` },

  { num: 3, tier: 'SCOUT', key: 'pestle_analysis', title: 'PESTLE Analysis', focus: 'Political, Economic, Social, Technology, Legal, Environmental - scored and implication-mapped.',
    prompt: `Produce a rigorous PESTLE Analysis for {COUNTRY} scoped specifically to the {SECTOR} sector. Avoid generic PESTLE bullet soup - every factor must connect to {SECTOR} economics.

REQUIRED STRUCTURE - six dimensions, each with this sub-structure:
For each of P / E / S / T / L / E:
- Current State (80-120 words): What is the situation today, quantified?
- 3-5 Year Trajectory: Is this getting better or worse for a {SECTOR} investor? Why?
- {SECTOR}-Specific Implication: How does this affect cost, revenue, risk, or speed-to-market for a {SECTOR} entrant?
- Impact Score: Rate as HIGH / MEDIUM / LOW impact on {SECTOR} opportunity.
- Direction: TAILWIND / HEADWIND / NEUTRAL.

REQUIRED DELIVERABLES:
- PESTLE Impact Matrix: 6-row markdown table - Dimension | Top Factor | Impact Score | Direction | Time Horizon.
- Tailwind vs Headwind tally with net assessment.

LENGTH: 1,500-1,800 words + matrix.
CLOSING: KEY TAKEAWAYS box: the 3 PESTLE factors that most shape the {SECTOR} investment thesis.` },

  { num: 4, tier: 'SCOUT', key: 'market_opportunity', title: 'Market Opportunity (TAM/SAM/SOM)', focus: 'Quantified market sizing with methodology, growth drivers, and service gaps.',
    prompt: `Quantify the {SECTOR} market opportunity in {COUNTRY} using a rigorous TAM > SAM > SOM framework. Show your math - methodology must be defensible in an IC meeting.

REQUIRED STRUCTURE:
1. Market Definition - Explicitly define what is in and out of scope for {SECTOR} in this analysis.
2. Total Addressable Market (TAM) - Top-down AND bottom-up estimate. Reconcile them. Present in USD and local currency.
3. Serviceable Addressable Market (SAM) - TAM filtered by geographic, regulatory, and infrastructure constraints. Show the filters applied and the resulting % of TAM.
4. Serviceable Obtainable Market (SOM) - Realistic share in Years 1, 3, 5 for a well-capitalized entrant, benchmarked against analog market entries.
5. Growth Drivers (Quantified) - 4-6 drivers, each with a measurable anchor.
6. Market Inhibitors - 3-4 factors that suppress TAM realization, each quantified.
7. Service & Supply Gaps - Where is current supply falling short of latent demand? Identify 3-5 specific gaps.
8. Benchmark Comparables - Size {SECTOR} market in {COUNTRY} against 3 peer countries.
9. 5-Year Forecast - TAM trajectory with base/bull/bear scenarios and CAGR.

REQUIRED DELIVERABLES: TAM/SAM/SOM pyramid. Growth drivers table. Peer benchmark table. 5-year forecast table with scenarios.

LENGTH: 1,200-1,500 words + tables.
CLOSING: KEY TAKEAWAYS box — the three numbers that matter most for sizing the opportunity.` },

  { num: 5, tier: 'SCOUT', key: 'government_capacity', title: 'Government Capacity & Fiscal Space', focus: 'Budget, payment mechanisms, fiscal risk, PPP readiness.',
    prompt: `Assess the Government of {COUNTRY}'s capacity and fiscal space to engage in {SECTOR} - with specific attention to whether the government can pay, co-invest, or underwrite a deal.

REQUIRED STRUCTURE:
1. Fiscal Position Snapshot — Revenue/GDP, expenditure/GDP, primary balance, overall balance, debt/GDP, debt service/revenue. 5-year trend.
2. {SECTOR} Budget Allocation — Last 3 years of budget allocation to {SECTOR}-relevant ministries/agencies. Capital vs. recurrent split. Execution rate.
3. Off-Budget & Contingent Liabilities — SOE debt, PPP contingent liabilities, guarantees issued.
4. Payment Mechanism Analysis — How do {SECTOR} contracts get paid? Payment reliability track record (any arrears)?
5. Multilateral & Bilateral Support — Active IMF program status, World Bank/AfDB/ADB {SECTOR} portfolio.
6. PPP Readiness — PPP law, dedicated PPP unit, pipeline, standardized contracts.
7. Fiscal Risk Assessment — Probability of government payment delays or renegotiation. Scored HIGH/MEDIUM/LOW.
8. Counterparty Mapping — Name the specific agencies/SOEs the entrant will contract with. Assess creditworthiness.

REQUIRED DELIVERABLES: Fiscal dashboard (10-row table with 5-year trend). PPP readiness scorecard. Counterparty risk table.

LENGTH: 1,200-1,400 words + tables. CLOSING: KEY TAKEAWAYS box.` },

  { num: 6, tier: 'SCOUT', key: 'competitive_landscape', title: 'Competitive Landscape', focus: "Market structure, operators, entry barriers, Porter's 5 Forces.",
    prompt: `Map the competitive landscape for {SECTOR} in {COUNTRY}. Deliver a view sharp enough that the reader could brief a board.

REQUIRED STRUCTURE:
1. Market Structure — Classify as Monopoly / Duopoly / Oligopoly / Fragmented / Contested. Quantify with HHI.
2. Operator Inventory — Top 10 operators: name, ownership, estimated market share, HQ, recent moves.
3. Strategic Groups — Cluster into 3-4 strategic groups.
4. Porter's Five Forces — Rigorous scoring (each HIGH/MEDIUM/LOW with evidence).
5. Entry Barriers — Quantify: capital intensity, licensing friction, local content requirements.
6. White Space Analysis — Where are incumbents NOT playing? 3-5 underserved segments.
7. Recent Market Activity — M&A, exits, new entrants in last 24 months.
8. Competitive Intensity Score — 1-10 scale with rationale.

REQUIRED DELIVERABLES: Operator matrix (10 rows). Porter's 5 Forces scorecard. White space map.

LENGTH: 1,500-1,800 words + matrices. CLOSING: KEY TAKEAWAYS box.` },

  { num: 7, tier: 'SCOUT', key: 'business_model', title: 'Business Model & Entry Architecture', focus: 'Entry mode options, revenue streams, phased rollout.',
    prompt: `Design a business model architecture for a {SECTOR} entrant in {COUNTRY}. Propose the optimal revenue model, cost structure, and phased rollout.

REQUIRED STRUCTURE:
1. Entry Mode Options (Decision Tree) — 4-6 entry modes with pros/cons/capital/speed/risk.
2. Recommended Entry Mode — Single recommendation with rationale and fallback.
3. Revenue Model Architecture — Primary and secondary revenue streams. Unit economics.
4. Cost Structure — Major cost lines, fixed vs variable, local vs imported, FX exposure.
5. Unit Economics — Illustrative per-unit P&L.
6. Phased Rollout Plan — Phase 1 (0-12mo) / Phase 2 (13-36mo) / Phase 3 (37-60mo) with objective, capex, milestones, go/no-go gates.
7. Analog Benchmarks — 2-3 case studies.
8. Moat / Defensibility.

REQUIRED DELIVERABLES: Entry mode comparison matrix. Phased rollout Gantt. Business Model Canvas.
LENGTH: 1,200-1,500 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 8, tier: 'SCOUT', key: 'risk_analysis', title: 'Risk Analysis', focus: 'Political, economic, operational, FX, regulatory risks with mitigations.',
    prompt: `Deliver a rigorous, IC-grade Risk Analysis for entering {SECTOR} in {COUNTRY}. Every risk must be scored, sourced, and paired with a specific mitigation.

REQUIRED RISK CATEGORIES (cover all):
1. Political Risk  2. Macro-Economic Risk  3. Sovereign & Counterparty Risk  4. Regulatory & Legal Risk  5. Operational Risk  6. Security Risk  7. Environmental & Climate Risk  8. Social & Reputational Risk  9. Technology & Cyber Risk  10. {SECTOR}-Specific Risk

FOR EACH RISK: Description (2-3 sentences), Likelihood (1-5), Impact (1-5), Risk Score (L x I), Time Horizon, Mitigation (concrete action), Residual Risk after mitigation.

REQUIRED DELIVERABLES: Risk Register (10+ rows). Risk Heat Map 5x5 grid. Top 5 Risks narrative. Black Swan callout.

LENGTH: 1,300-1,600 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 9, tier: 'SCOUT', key: 'financial_projections', title: 'Financial Projections', focus: 'CAPEX, 10-year P&L, IRR, NPV, payback, scenarios.',
    prompt: `Build a defensible financial model for a {SECTOR} entrant in {COUNTRY}. Every number must have a visible assumption and source.

REQUIRED STRUCTURE:
1. Assumption Book (Assumption | Value | Source | Sensitivity).
2. CAPEX Build-Up.
3. Revenue Build-Up — bottom-up volume × price × mix, 10 years.
4. Operating Cost Build-Up.
5. 10-Year P&L.
6. Cash Flow Statement.
7. Key Metrics — IRR, NPV, Payback, DSCR, Debt/EBITDA, ROIC.
8. Scenarios — Bull/Base/Bear.
9. Funding Structure.

REQUIRED DELIVERABLES: Assumption table. 10-year P&L. Key metrics dashboard. Funding stack.
LENGTH: 1,400-1,700 words. CLOSING: KEY TAKEAWAYS box.
IMPORTANT: State clearly that projections are illustrative models based on disclosed assumptions.` },

  { num: 10, tier: 'SCOUT', key: 'sovereign_risk', title: 'Sovereign Risk', focus: 'Credit ratings, debt sustainability, macro stability, default history.',
    prompt: `Deliver a Sovereign Risk Assessment for {COUNTRY}, anchored to the concerns of a {SECTOR} investor.

REQUIRED STRUCTURE:
1. Current Sovereign Ratings — S&P, Moody's, Fitch (LT foreign/local + outlooks + latest action).
2. Rating Trajectory (5-Year) — What drove the moves?
3. Debt Sustainability — Public debt/GDP, external debt/GDP, debt service/revenue, FX-denominated share, avg maturity. IMF DSA.
4. IMF Engagement.
5. External Position — CA, NIIP, FX reserves (absolute + months imports + reserves/ST debt).
6. Currency Regime.
7. Default & Restructuring History — Last 20 years.
8. Banking System Health.
9. Political Stability Indicators.
10. {SECTOR}-Linkage.

REQUIRED DELIVERABLES: Sovereign rating table. Debt dashboard. EMBI spread. DSA summary.
LENGTH: 1,200-1,500 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 11, tier: 'SCOUT', key: 'social_impact', title: 'Social Impact & ESG', focus: 'Jobs, SDG alignment, ESG scoring, community impact.',
    prompt: `Assess the social impact and ESG profile of a {SECTOR} investment in {COUNTRY}.

REQUIRED STRUCTURE:
1. Employment Impact — Direct / indirect / induced jobs. Gender disaggregation. Skill mix.
2. Economic Multipliers — GDP, tax revenue, FX, import substitution.
3. SDG Alignment — 5-8 UN SDGs with causal links.
4. IFC Performance Standards Alignment.
5. Environmental Footprint — GHG (scope 1/2/3), water, waste, biodiversity.
6. Community Engagement — FPIC, grievance mechanism, local content.
7. Gender Lens — 2X Challenge alignment.
8. Just Transition Considerations.
9. ESG Scoring — E/S/G with peer benchmark.
10. Impact Measurement Framework — 5-7 HIPSO/IRIS+ KPIs.

REQUIRED DELIVERABLES: SDG alignment table. Jobs multiplier table. ESG scorecard.
LENGTH: 1,200-1,500 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 12, tier: 'SCOUT', key: 'governance', title: 'Governance & PPP Readiness', focus: 'Institutional framework, regulatory quality, PPP infrastructure.',
    prompt: `Assess the governance environment of {COUNTRY} as it bears on a {SECTOR} investment.

REQUIRED STRUCTURE:
1. Institutional Framework — Key ministries, agencies, regulators. Regulator independence.
2. Rule of Law & Contract Enforcement.
3. Regulatory Quality — WGI + {SECTOR}-specific predictability.
4. Corruption & Integrity — CPI rank, TRACE Bribery Risk.
5. {SECTOR} Regulatory Body — Governance, independence, recent controversial decisions.
6. PPP Legal & Institutional Framework — Infrascope/WB PPP Readiness.
7. Investor Protection — FDI law, MIGA/ATI guarantees, BITs, ICSID.
8. Transparency & Access to Information.
9. Judicial Recourse — Commercial courts, arbitration, New York Convention.
10. Recent Governance Events — Last 24 months.

REQUIRED DELIVERABLES: Governance scorecard. PPP readiness diagnostic. Investor protection summary.
LENGTH: 1,200-1,400 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 13, tier: 'STRATEGIST', key: 'stakeholder_map', title: 'Stakeholder & Power Map', focus: 'Influence-interest grid of decision-makers, blockers, enablers.',
    prompt: `Produce a Stakeholder & Power Map for a {SECTOR} investment in {COUNTRY}. The output must answer: "Who do I need to meet, in what order, and what do they want?"

1. Stakeholder Universe — 15-25 stakeholders across Government / Multilateral/DFI / Private / Civil society / External.
2. Power-Interest Grid (2x2) — Manage Closely / Keep Satisfied / Keep Informed / Monitor.
3. Decision-Maker Directory — For high-power stakeholders.
4. Coalition Analysis — Pro-deal, anti-deal, swing bloc.
5. Blocker-Enabler Map — Top 5 each with neutralization/activation strategies.
6. Political Economy of {SECTOR}.
7. Engagement Plan (90-day).
8. Relationship Risk Watch.

DELIVERABLES: Power-Interest grid. 15-25 row directory. 90-day roadmap.
LENGTH: 1,400-1,700 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 14, tier: 'STRATEGIST', key: 'regulatory_deep_dive', title: 'Regulatory & Compliance Deep Dive', focus: 'Licensing pathway, permits, standards, compliance roadmap.',
    prompt: `Deliver a Regulatory & Compliance Deep Dive for {SECTOR} entry in {COUNTRY}. Function as a compliance blueprint.

1. Regulatory Architecture.
2. Licensing Pathway (Step-by-Step) — every license in sequence with issuing authority, prerequisites, timeline, fee, pitfalls.
3. Sector-Specific Technical Standards.
4. Environmental & Social Safeguards (EIA/ESIA thresholds).
5. Data & Digital Compliance.
6. Local Content & Indigenization Requirements.
7. Tax Registration & Recurrent Compliance.
8. Labor Compliance.
9. Monitoring, Reporting & Audit Obligations.
10. Enforcement Risk.

DELIVERABLES: Licensing Gantt. Compliance cost stack. Compliance calendar.
LENGTH: 1,500-1,800 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 15, tier: 'STRATEGIST', key: 'value_chain', title: 'Sector Value Chain & Margin Pools', focus: 'Upstream to downstream with profit pool analysis.',
    prompt: `Map the {SECTOR} value chain in {COUNTRY} and locate the profit pools.

1. End-to-End Value Chain Map (6-10 stages).
2. Stage-by-Stage Analysis — activities, players, scale, EBITDA margin, capex, barriers, tech.
3. Margin Pool Visualization.
4. Global Benchmark.
5. Value Chain Integration Patterns.
6. Disruption Watch.
7. Strategic Playing Field — Volume / Margin / Integrator play.
8. Local Content Unlock.

DELIVERABLES: Value chain diagram. Stage-by-stage table. Profit pool bar chart.
LENGTH: 1,400-1,700 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 16, tier: 'STRATEGIST', key: 'tech_readiness', title: 'Technology & Digital Readiness', focus: 'Infrastructure, connectivity, digital maturity, adoption curves.',
    prompt: `Assess {COUNTRY}'s technology and digital readiness as it affects {SECTOR} economics.

1. Digital Infrastructure Baseline — Mobile, 3G/4G/5G, broadband, data costs (GSMA, ITU, Ookla).
2. Power & Connectivity for Operations.
3. Digital Public Infrastructure — National ID, digital payments, e-signature.
4. Fintech & Payments.
5. Cloud & Data Centers.
6. GovTech Maturity (WB GovTech Maturity Index).
7. {SECTOR}-Specific Tech Adoption.
8. AI, IoT & Emerging Tech.
9. Talent & Innovation Ecosystem.
10. Cybersecurity Posture.

DELIVERABLES: Digital Readiness Scorecard. {SECTOR} Digitization Gap analysis.
LENGTH: 1,200-1,500 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 17, tier: 'STRATEGIST', key: 'entry_strategy', title: 'Entry Strategy Playbook', focus: 'Entry mode decision tree with phasing logic.',
    prompt: `Produce a decision-ready Entry Strategy Playbook for {SECTOR} in {COUNTRY}.

1. Entry Objective Clarification.
2. Entry Mode Decision Tree.
3. Partner Landscape — Top 10 (SOEs, private, PE-backed).
4. Partner Selection Criteria — weighted scorecard.
5. JV Structuring Norms.
6. Incorporation & Setup Roadmap.
7. Government Engagement Sequence.
8. First 90 Days In-Country.

DELIVERABLES: Partner scorecard. Decision tree. 90-day action plan.
LENGTH: 1,400-1,700 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 18, tier: 'STRATEGIST', key: 'local_partners', title: 'Local Partner & JV Landscape', focus: 'Partner identification, due diligence framework, JV structuring.',
    prompt: `Map the Local Partner & JV Landscape for {SECTOR} in {COUNTRY}.

1. Partner Typology — Industrial, conglomerates, SOE-linked, PE/VC-backed, family offices, PSFs.
2. Long List (15-20).
3. Short List (5) — leadership, financials, strategic fit, cultural alignment, governance, ESG posture.
4. Due Diligence Framework — 10-dimension checklist.
5. JV Structuring Norms.
6. Relationship Initiation Protocol.
7. Red Flags & Disqualifiers — sanctions, BO opacity, litigation, PEP exposure.
8. Partnership Success Factors.

DELIVERABLES: Long list table. Short list profiles. DD checklist.
LENGTH: 1,400-1,700 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 19, tier: 'STRATEGIST', key: 'scenario_planning', title: 'Scenario Planning & Stress Testing', focus: 'Bull/Base/Bear scenarios with trigger events and decision gates.',
    prompt: `Develop a Scenario Planning & Stress Testing framework for {SECTOR} investment in {COUNTRY}.

1. Scenario Architecture — Bull / Base / Bear with narrative, probability, key assumptions, macro parameters.
2. Macro Variable Sensitivity — GDP ±2pp, inflation ±5pp, FX 10/20/30% depreciation, rates ±3pp, demand ±20%.
3. Sector-Specific Stress Tests.
4. Political Scenario Overlay.
5. Combined Stress Scenario ("Perfect Storm").
6. Breakeven Analysis.
7. Decision Gates.
8. Early Warning Indicators — 10 KPIs with trigger thresholds.
9. Contingency Playbook.

DELIVERABLES: 3-scenario table. Tornado chart. Decision gate framework. Early warning dashboard.
LENGTH: 1,400-1,700 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 20, tier: 'STRATEGIST', key: 'tax_incentives', title: 'Tax, Incentive & Structuring Guide', focus: 'Tax regime, incentives, SEZs, holding structures, DTAAs.',
    prompt: `Deliver a Tax, Incentive & Structuring Guide for {SECTOR} investment in {COUNTRY}.

1. Headline Tax Rates — CIT, VAT/GST, WHT (dividends/interest/royalties/services — with DTAA reductions), CGT, Branch Profits.
2. {SECTOR}-Specific Levies.
3. Incentive Regime — Tax holidays, accelerated depreciation, investment credits, customs exemptions, R&D, export, local content.
4. Special Economic Zones.
5. Investment Promotion Authority.
6. Tax Treaties (DTAAs) — MLI status.
7. Transfer Pricing — APA availability.
8. Indirect Tax & Customs.
9. Repatriation & Exchange Control.
10. Recent Tax Reforms & Pipeline — Pillar Two.

DELIVERABLES: Headline rates table. Incentives matrix. Illustrative optimization structure.
LENGTH: 1,200-1,500 words. CAUTION: Informational, not tax advice. CLOSING: KEY TAKEAWAYS box.` },

  { num: 21, tier: 'STRATEGIST', key: 'talent_labor', title: 'Talent & Labor Market', focus: 'Skill availability, wage benchmarks, unions, expat rules.',
    prompt: `Assess the Talent & Labor Market for {SECTOR} in {COUNTRY}.

1. Labor Market Snapshot.
2. Skill Supply for {SECTOR}.
3. Wage Benchmarks — Executive, Senior Mgmt, Middle Mgmt, Specialist, Technician, Admin, Unskilled.
4. Total Employment Cost — social security, pension, health, training levies.
5. Expatriate Employment — work permits, processing, quotas, localization.
6. Labor Law Highlights.
7. Union Landscape.
8. Skills Development Infrastructure.
9. Diversity & Inclusion.
10. Attrition & Retention Dynamics.

DELIVERABLES: Wage scorecard (7-9 roles). Labor compliance checklist. Talent gap analysis.
LENGTH: 1,200-1,500 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 22, tier: 'STRATEGIST', key: 'supply_chain', title: 'Supply Chain & Logistics Assessment', focus: 'Ports, corridors, import lead times, localization opportunities.',
    prompt: `Deliver a Supply Chain & Logistics Assessment for {SECTOR} operations in {COUNTRY}.

1. Country Logistics Performance (WB LPI).
2. Ports & Gateways (TEU, dwell time).
3. Transport Corridors.
4. Customs & Trade Facilitation.
5. Warehousing & Cold Chain.
6. Last-Mile Distribution.
7. {SECTOR} Supply Chain Map.
8. Localization Opportunity.
9. Regional Integration — AfCFTA, ECOWAS, EAC, SADC, GCC, ASEAN.
10. Disruption Resilience.

DELIVERABLES: Logistics cost stack. Corridor map. Lead time analysis.
LENGTH: 1,200-1,500 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 23, tier: 'SOVEREIGN', key: 'dfi_landscape', title: 'DFI & Blended Finance Landscape', focus: 'Active DFI pipeline, matched to deal profile.',
    prompt: `Map the DFI & Blended Finance Landscape relevant to a {SECTOR} transaction in {COUNTRY}.

1. DFI Universe — IFC, WB, AfDB, ADB, EBRD, EIB, IsDB, USDFC, BII, FMO, DEG, Proparco, Norfund, JBIC + climate finance.
2. DFI-by-DFI Profile (top 8-10) — country strategy, sector portfolio, ticket, instruments, E&S standards.
3. Blended Finance Instruments — first-loss, subordinate debt, grants, guarantees.
4. Climate & Sustainable Finance — green bonds, CIF/GCF.
5. Guarantee & Credit Enhancement — MIGA PRI, ATI, GuarantCo, InfraCredit, ECAs.
6. Concessional Window Eligibility — IDA/IBRD, LDC facilities.
7. DFI Match Matrix — Geographic / Sector / Ticket / Instrument / Impact fit.
8. Process & Timeline.
9. Policy Bank & ECA Support — Chinese, Japanese, Korean, Indian.
10. Precedent Transactions.

DELIVERABLES: DFI Match Matrix. Blended finance stack. DFI outreach sequence.
LENGTH: 1,200-1,500 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 24, tier: 'SOVEREIGN', key: 'deal_structuring', title: 'Deal Structuring & PPP Playbook', focus: 'Concession structures, off-take models, risk-sharing templates.',
    prompt: `Produce a Deal Structuring & PPP Playbook for a {SECTOR} transaction in {COUNTRY}.

1. Structural Archetype Selection — BOT/BOOT/BOO/BLT, Concession, Availability-payment PPP, Off-take/PPA, IPP.
2. Typical Term Structure.
3. Revenue Mechanism Design — user-pay, government-pay, min revenue guarantee, take-or-pay, indexation.
4. Risk Allocation Matrix — 15+ risks allocated across Government / Project Co / Lenders / Off-taker / Insurance.
5. Credit Support Architecture — sovereign guarantee, MIGA PRI, DFI PRG, escrow, liquidity facility.
6. Tariff Setting & Adjustment.
7. Exit & Handback.
8. Dispute Resolution — seat, governing law, ICSID/UNCITRAL.
9. Precedent Deal Structures.
10. Indicative Term Sheet Outline — 15-20 line headers.

DELIVERABLES: Risk allocation matrix. Credit support stack. Indicative term sheet.
LENGTH: 1,200-1,500 words. CAUTION: Requires legal counsel. CLOSING: KEY TAKEAWAYS box.` },

  { num: 25, tier: 'SOVEREIGN', key: 'ma_targets', title: 'M&A and Asset Acquisition Targets', focus: 'Screened targets with indicative valuations and access paths.',
    prompt: `Identify and profile M&A and Asset Acquisition Targets in {SECTOR} in {COUNTRY}. NOTE: Public information only.

1. Target Universe Definition.
2. Target Typology — Scale, Bolt-On, Distressed, SOE Divestiture, Foreign Exit.
3. Target Long List (12-20).
4. Short-List (5) — expanded profiles.
5. Valuation Benchmarks — EV/Revenue, EV/EBITDA.
6. Regulatory Approvals for M&A.
7. Deal Process Norms.
8. Integration Risk.
9. Divestiture Pipeline Watch.
10. Outreach Protocol.

DELIVERABLES: Long list. Short list (5). Valuation comps.
LENGTH: 1,400-1,700 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 26, tier: 'SOVEREIGN', key: 'geopolitical_brief', title: 'Geopolitical & Security Intelligence Brief', focus: 'Conflict, election cycles, elite dynamics, regional spillovers.',
    prompt: `Produce a Geopolitical & Security Intelligence Brief for {COUNTRY} scoped to a {SECTOR} investor's 5-10 year horizon.

1. Political Regime & Stability.
2. Election Cycle — contenders, policy continuity scenarios.
3. Elite Political Economy.
4. Internal Security — ACLED data, unrest, crime, state capacity.
5. Regional & Border Dynamics.
6. Great Power Competition — US-China-Russia, BRI, OFAC/EU sanctions.
7. Sanctions & Compliance Risk — FATF grey/black list.
8. Climate & Natural Disaster Exposure.
9. Civil Society & Media.
10. Outlook (5-Year Scenarios) — Continuity / Reform / Retrenchment / Rupture.

DELIVERABLES: Security heat map. Election timeline. Scenario probability table.
LENGTH: 1,500-1,800 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 27, tier: 'SOVEREIGN', key: 'diplomatic_tracker', title: 'Diplomatic & Bilateral Relations Tracker', focus: 'BITs, trade agreements, dispute history, bilateral programs.',
    prompt: `Deliver a Diplomatic & Bilateral Relations Tracker for {COUNTRY}.

1. BIT Network — in-force BITs, FET/MFN/expropriation/ISDS protections.
2. Trade Agreements — WTO, AfCFTA, ECOWAS, EAC, SADC, GCC, ASEAN; AGOA, GSP.
3. Tax Treaty Network — DTAAs + MLI positions.
4. ICSID Membership & Arbitration History.
5. Investment Dispute History — last 10 years.
6. Bilateral Development Programs.
7. Visa & Business Travel.
8. Relationship Outlook.

DELIVERABLES: BIT/DTAA matrix. Dispute history. Hold-co jurisdiction recommendation.
LENGTH: 1,200-1,500 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 28, tier: 'SOVEREIGN', key: 'investment_memo', title: 'Board-Ready Investment Memo', focus: 'IC-grade go/no-go memo synthesizing prior sections.',
    prompt: `Write a Board-Ready IC Investment Memo for the {COUNTRY} - {SECTOR} opportunity.

WRITING STYLE: MD-level IC memo. Zero fluff.

STRUCTURE:
1. RECOMMENDATION (50 words) — Approve / Approve with conditions / Defer / Decline with rationale.
2. DEAL AT A GLANCE (table) — Country, Sector, Deal size, IRR (3 scenarios), MOIC, Payback, Counterparty.
3. INVESTMENT THESIS (200 words) — Three pillars with evidence + quantified anchors.
4. MARKET OPPORTUNITY (100 words) — TAM/SAM/SOM.
5. COMPETITIVE POSITIONING (80 words) — right to win.
6. DEAL STRUCTURE & RETURNS (150 words).
7. RISKS & MITIGANTS (200 words) — Top 5 with mitigants. 1 deal-killer.
8. ESG / IMPACT (80 words).
9. CONDITIONS PRECEDENT — 5-8 CPs.
10. DECISION QUESTIONS FOR IC — 3-5.

DELIVERABLES: Deal-at-a-glance box. 3-scenario returns table. Top-5 risks table. CP list.
LENGTH: 800-1,000 words. State uncertain figures as "indicative, subject to diligence."` },

  { num: 29, tier: 'SOVEREIGN', key: 'portfolio_benchmark', title: 'Multi-Country Portfolio Benchmark', focus: 'Cross-country heatmap comparing against pipeline.',
    prompt: `Produce a Multi-Country Portfolio Benchmark placing the {COUNTRY} - {SECTOR} opportunity against 4-7 peer countries.

1. Peer Country Selection Rationale.
2. Benchmark Dimensions (1-10) — TAM, Growth, Competitive intensity (inverted), Regulatory quality, Government capacity, Sovereign risk, Ease of entry, Currency stability, DFI availability, ESG profile.
3. Composite Attractiveness Score — weighted.
4. Tier Classification — Tier 1 / Tier 2 / Tier 3.
5. Opportunity Heat Map — 10 × peers.
6. Comparative Unit Economics.
7. Relative Risk-Return Position — 2D scatter.
8. Diversification Logic.
9. Capital Deployment Sequence — 24-36 months.
10. Portfolio Construction Insight.

DELIVERABLES: Heat map. Composite scorecard. Risk-return scatter. Deployment sequence.
LENGTH: 1,400-1,700 words. CLOSING: KEY TAKEAWAYS box.` },

  { num: 30, tier: 'SOVEREIGN', key: 'execution_playbook', title: '100-Day Entry & Execution Playbook', focus: 'Week-by-week roadmap for first 100 days post-commitment.',
    prompt: `Build a 100-Day Entry & Execution Playbook for a {SECTOR} investor committed to {COUNTRY}.

WEEKLY MILESTONES (14 weeks, Day 1-100):
- Week 1: Company/bank/legal setup
- Week 2: Core team hired
- Weeks 3-4: Stakeholder launch tour
- Weeks 5-6: Licensing & regulatory filings
- Weeks 7-8: Key site/asset secured
- Weeks 9-10: First hires, partner contracts
- Weeks 11-12: Operational readiness, systems, pilot
- Weeks 13-14: Pilot/soft launch, Day 100 review

PARALLEL WORKSTREAMS (each with owner, deliverables, Day-100 target):
1. Legal & Corporate  2. Regulatory & Licensing  3. Government Relations  4. Human Capital  5. Finance & Treasury  6. Tax & Compliance  7. Operations / Supply Chain  8. Technology  9. Marketing & Communications  10. ESG & Community

DAY 100 REVIEW: Regulatory status, P&L vs plan, stakeholder log, risk register update, 200-day plan.

DELIVERABLES: Week-by-week Gantt. RACI matrix. Day 100 scorecard (15 KPIs). Decision rights matrix.
LENGTH: 1,200-1,500 words. CLOSING: KEY TAKEAWAYS box.` },
  { num: 31, tier: 'SOVEREIGN', key: 'capital_markets', title: 'Capital Markets & Listing Pathways', focus: 'Equity capital markets, debt issuance, listing venues, exit pathways.',
    prompt: `Map the Capital Markets & Listing Pathways relevant to a {SECTOR} investor seeking to raise capital, list, or exit a position in {COUNTRY}. Output an investment-grade brief covering local listing venues, cross-border options, debt markets, and realistic exit timelines.

REQUIRED SUBSECTIONS:

1. LOCAL EQUITY CAPITAL MARKETS — Principal stock exchange(s) of {COUNTRY}: market capitalisation, daily turnover, number of listed companies, foreign-investor participation %, free float thresholds. Sector representation (specifically {SECTOR}-listed peers, with current market caps and 12-month performance). Sources: World Federation of Exchanges, exchange disclosure filings, S&P Capital IQ.

2. LISTING REQUIREMENTS — Main board vs. growth/SME/AltX-equivalent boards. Minimum market cap, profitability track record, free float %, post-listing lockup, corporate governance code. Time-to-list and indicative cost (advisor + regulatory fees). Disclosure regime.

3. CROSS-BORDER LISTING OPTIONS — DR programmes (ADRs/GDRs), Level 1/2/3 sponsored programmes, dual-listing precedents from {COUNTRY}-domiciled companies on LSE / NYSE / NASDAQ / SGX / JSE. Local content rules, capital-flow and FX repatriation considerations.

4. DEBT CAPITAL MARKETS — Sovereign yield curve (3M / 1Y / 5Y / 10Y / 30Y), sovereign external bond spreads vs. EMBI Global. Corporate bond market depth (annual issuance, top-5 issuers in {SECTOR}). Eurobond access for {COUNTRY} corporates: tenors, coupons, covenants, ratings notch differential to sovereign.

5. PRIVATE PLACEMENT & MEZZANINE — Reg S / 144A pathways, local-currency vs. hard-currency. Private credit funds active in {SECTOR}-{COUNTRY}, typical ticket and pricing.

6. SUKUK & ISLAMIC FINANCE (if applicable) — Issuance volume, structures (ijara, murabaha, sukuk-al-wakala), pricing differential vs conventional.

7. GREEN / SUSTAINABILITY-LINKED ISSUANCE — ICMA Green Bond Principles eligibility, CBI certification track record, sustainability-linked bond / loan precedents.

8. EXIT PATHWAYS BY TIMELINE — IPO market readiness (3-6 year horizon, 5 closest-comp benchmarks). Strategic-trade-sale precedents (last 5 deals in {SECTOR}-{COUNTRY}, transaction multiples, acquirer geographies). Secondary buy-out / continuation-fund options. Recapitalisation / dividend recap precedents.

9. INVESTOR BASE & LIQUIDITY — Domestic institutional investor pool (pension funds, asset managers, insurance companies AUM), foreign portfolio investor share %, retail participation %. Daily turnover ratio, bid-ask spreads on {SECTOR} comps.

10. REGULATORY ENVIRONMENT — Securities regulator (SEC-equivalent), insider trading regime, takeover code, REIT / InvIT framework if applicable.

DELIVERABLES: Listing decision tree (local main board vs. growth board vs. cross-border DR vs. private placement). Yield curve + bond peer comp table. Exit pathway matrix (IPO / trade sale / secondary, with 18-24 / 36-48 / 60-72 month time-to-exit estimates). 5 closest-comp transaction precedents with multiples.
LENGTH: 1,500-1,800 words. CLOSING: KEY TAKEAWAYS box (3-5 bullets, single most attractive capital-market pathway, time-to-execute, back-of-envelope cost).` },
];

// Inline prompts from /app/backend/server.py
export const AI_STUDIO_PROMPTS = [
  {
    label: 'PDF · Executive Investment Brief',
    model: 'Claude Sonnet 4.5',
    system: "You are a Managing Director at a tier-one global strategy consulting firm with 50 years of sovereign advisory experience. You also served as Chief Economist at the World Bank. Your writing blends senior-partner pyramid-principle structure with the World Bank's evidence-based rigor. Be precise, quantitative, and authoritative.",
    prompt: `Based on this complete country intelligence report, write an EXECUTIVE INVESTMENT BRIEF for {COUNTRY} — {SECTOR} sector.

FORMAT (strict — this goes on a single PDF page):

## INVESTMENT VERDICT
[One sentence: STRONG BUY / BUY / HOLD / CAUTIOUS / AVOID — with 1-line rationale]

## KEY FINDINGS
- [Finding 1 with specific number/data point]
- [Finding 2 with specific number/data point]
- [Finding 3 with specific number/data point]
- [Finding 4 with specific number/data point]
- [Finding 5 with specific number/data point]

## OPPORTUNITY SNAPSHOT
| Metric | Assessment |
|---|---|
| Market Size | $X billion (TAM) |
| Entry Window | [Timing assessment] |
| Risk Rating | [Low/Medium/High with score X/10] |
| Expected IRR | [X-Y% range] |
| Payback Period | [X years] |
| Competitive Intensity | [Assessment] |

## STRATEGIC RECOMMENDATION
[Senior-partner-style recommendation: 3-4 sentences with specific next steps, phasing, and investment quantum]

REPORT DATA:
{summary[:8000]}`,
  },
  {
    label: 'Sources & References',
    model: 'Claude Sonnet 4.5',
    system: 'You are a tier-one consulting research director compiling authoritative sources for an investment-grade country analysis report. Number all references sequentially.',
    prompt: `Based on the following country blueprint analysis for {COUNTRY} ({SECTOR} sector), compile a comprehensive SOURCES & REFERENCES section.

Blueprint content:
{summary[:3000]}

List ALL data sources, databases, reports, and institutions whose data would have been used to produce this analysis. Organize by category. Include specific report titles, years, and URLs where possible.

IMPORTANT: Number every single reference sequentially (1, 2, 3, ... through the entire list). Use this format:

## International Organizations & Databases
1. World Bank Open Data — GDP, population, urbanization statistics, 2024 edition
2. IMF World Economic Outlook, October 2024
3. [continue numbering...]

## {SECTOR} Sector Reports
## Government & Regulatory Sources
## Financial & Investment Data
## Industry & Market Intelligence
## Academic & Research Publications

Be specific — include actual report names, editions, and URLs. List 30-40 numbered references total. The numbering must be continuous across all categories.`,
  },
  {
    label: 'Audio Overview · Narration Script',
    model: 'Claude Sonnet 4.5 → OpenAI TTS (tts-1-hd, voice: onyx)',
    system: 'You are a senior tier-one consulting economist writing professional investment podcast narration. Clear, authoritative, data-backed.',
    prompt: `Write a 2-minute professional narration script (under 3500 characters) for an investment briefing podcast about {COUNTRY} for the intelligence unit.

Use this blueprint data:
{summary[:3000]}

Structure: Opening hook -> Country overview -> Market opportunity -> Key risks -> Financial outlook -> Closing recommendation.
Tone: Authoritative but engaging, like a senior strategy partner briefing investors.
Do NOT include speaker labels or stage directions. Write only the spoken narration text.`,
  },
  {
    label: 'Cinematic Brief · Narration Script (UI disabled, V2)',
    model: 'Claude Sonnet 4.5',
    system: 'You are a senior tier-one consulting economist writing professional documentary narration for sovereign investment briefings. Authoritative, data-rich, and compelling.',
    prompt: `Write a professional documentary narration script for a video about {COUNTRY}'s investment landscape for the intelligence unit.

Blueprint data:
{summary[:3500]}

IMPORTANT: Write a THOROUGH narration that covers ALL key points from the blueprint. The script MUST be under 3800 characters (this is a hard limit for the text-to-speech engine).

Structure:
1. Opening hook about {COUNTRY}
2. Current sector landscape and challenges
3. PESTLE factors affecting the market
4. Market opportunity and sizing
5. Government capacity and funding
6. Competitive landscape
7. Business model recommendations for the unit
8. Key risks and mitigation
9. Financial projections and ROI
10. Closing: Investment recommendation

Tone: Authoritative documentary, like a senior strategy partner briefing institutional investors.
Do NOT include speaker labels, stage directions, or scene markers. Write ONLY the spoken narration text.`,
  },
  {
    label: 'Cinematic Brief · Scene Prompts (UI disabled, V2)',
    model: 'Claude Sonnet 4.5 → Sora 2',
    system: 'You are a professional documentary film director creating shot lists.',
    prompt: `Create exactly {N} cinematic scene prompts for a {AUDIO_DURATION}-second professional documentary video about {COUNTRY}'s investment landscape for the intelligence unit.

Blueprint data:
{summary[:2000]}

Return ONLY a JSON array of {N} objects, each with "scene_num", "title", and "prompt" fields.

Rules for each prompt:
- Use the formula: [Subject] + [Action] + [Environment] + [Camera movement] + [Lighting] + [Style]
- Keep consistent across ALL scenes: same city ({COUNTRY}), same corporate blue/green color tone, same documentary style
- Each clip will be 8 seconds; ultra-realistic, 4K, cinematic documentary style

Distribute scenes across this narrative arc:
- Scene 1: Sweeping aerial establishing shot of {COUNTRY} at golden hour
- Mid scenes: The challenge — current infrastructure, gaps, urban sprawl
- Next block: The opportunity — markets, population, growth
- Next block: Current & future operations — modern facilities, technology
- Next block: Investment vision — green technology, engineered infrastructure
- Last scene: Hopeful outro — clean modern city, sunrise, progress

Return ONLY valid JSON array, no other text.`,
  },
];

export const ILLUSTRATION_PROMPTS = [
  {
    label: 'Gemini · Per-section illustration (primary)',
    model: 'Gemini Nano Banana',
    attempt: 1,
    prompt: `Professional business infographic for {COUNTRY} {SECTOR} sector section '{SECTION}'. Clean flat design, light-blue corporate palette, icons and chart elements, no text overlay.`,
  },
  {
    label: 'Gemini · Per-section illustration (fallback 2)',
    model: 'Gemini Nano Banana',
    attempt: 2,
    prompt: `Modern business infographic about {COUNTRY}, flat design, corporate palette, no text.`,
  },
  {
    label: 'Gemini · Per-section illustration (fallback 3)',
    model: 'Gemini Nano Banana',
    attempt: 3,
    prompt: `Clean corporate infographic, light blue palette, {COUNTRY} theme, no text.`,
  },
  {
    label: 'Gemini · Full-page Investment Infographic',
    model: 'Gemini Nano Banana',
    prompt: `Create a comprehensive one-page investment infographic for {COUNTRY} {SECTOR} market entry for the intelligence unit. Include: Large header with '{COUNTRY} {SECTOR} MARKET ENTRY' title, key statistics in bold callout boxes (population, market size, growth rate, key metrics), a hexagonal PESTLE analysis diagram with 6 factors, a market sizing funnel (TAM to SAM to SOM), a traffic-light risk matrix (political/economic/operational rated red/yellow/green), a 5-year revenue projection bar chart, a 3-phase entry timeline diagram, and intelligence branding with 'Sovereign IQ' watermark. Professional blue and green corporate color palette, data-rich business intelligence infographic style. Modern clean design suitable for investment committee presentations.`,
  },
  {
    label: 'Claude HTML · Illustration Fallback (Admin-toggled)',
    model: 'Claude Sonnet 4.6 → Playwright Chromium → PNG',
    system: 'You are an investment-grade infographic designer. You produce only self-contained HTML+inline-SVG documents. You never use external JS, raster images, or markdown prose. Your output begins with <!DOCTYPE html> and contains no commentary.',
    prompt: `Create a single-page HTML infographic illustration for a Sovereign IQ country investment blueprint with the following specifications:

CONTEXT:
- Country: {COUNTRY}
- Sector: {SECTOR}
- Section: {SECTION_TITLE}
- Focus: {SECTION_FOCUS}

VISUAL STYLE:
- Dark {palette.bg} background for the outer page and header band
- Light cream/off-white (#e8dfc0) panel backgrounds for content sections
- {palette.accent} as the primary highlight/badge color
- Font pairing: Barlow Condensed (headers, numbers, labels) + Barlow (body text) loaded from Google Fonts
- No gradients on backgrounds; flat layered surfaces only
- Rounded panels (border-radius: 8px) with subtle border separation

LAYOUT STRUCTURE (STRICT — DO NOT EXCEED):
- Fixed width: 900px, centered on page
- TOTAL HEIGHT MUST BE <= 1180px so the whole infographic fits A4 at ~0.64 scale
- Header bar: logo icon (simple SVG) + eyebrow subtitle "SOVEREIGN IQ" + bold main title from the section name
- Top row: 2-column grid (left panel ~45%, right panel ~55%)
- Full-width middle section
- Full-width lower section
- Bottom summary bar (label left + mini chart right)
- Footer strip: "www.sovereigniq.org" | "Confidential — Investment Committee Use Only" | "{COUNTRY}"

SECTION HEADERS:
- Colored icon box (28x28px, rounded) + section number + ALL-CAPS Barlow Condensed 800 title + small muted source citation

VISUAL COMPONENTS TO USE (pick 3-5 per section — NEVER include photos, only SVG shapes):
3D stacked bar / mini grouped bar charts / SVG map silhouette with teardrop pins / horizontal progress bars / metric pills / comparison bar chart / donut or ring chart / tile grid of KPIs.

DATA TO POPULATE:
- Realistic quantitative placeholders grounded in public macro data for {COUNTRY} {SECTOR} (GDP growth, FDI, sector share, ratings). Tiny source citation under each chart.

COLOR VARIABLES (define as CSS vars at top of <style>):
  --bg        --bg-2      --card     --card-2
  --accent    --accent-2  --teal     --teal-lt  --ink  --ink-2

OUTPUT REQUIREMENTS:
- Return ONE single self-contained HTML document
- NO external JS, NO <script> tags, NO frameworks
- All SVG inline, all styles in a single <style> block
- NO <img> tags — all visuals must be SVG
- Wrap in <div class="page" style="width:900px; min-height:1100px; margin:0 auto;">
- Body margin must be 0
- Return ONLY the HTML — no markdown fences, no explanation, no comments before <!DOCTYPE html>`,
  },
];

export const PIPELINE_NOTES = [
  { label: 'Chunked generation', detail: 'Every section is generated in 2 streaming calls — Part A (~800w) and Part B (~500w body + 200w KEY TAKEAWAYS box + "--- END OF SECTION ---" sentinel). Self-healing completion call triggers if the sentinel is missing. Keeps each call under the 60s Emergent proxy ceiling.' },
  { label: 'Per-country lock', detail: 'asyncio.Lock registry prevents parallel section generations on the same country. Concurrent requests return "queued" with a live queue-ahead count.' },
  { label: 'Illustration fallback chain', detail: 'Gemini is tried with 3 prompt variants (detailed → intent-preserving → generic safe). If the admin has toggled to Claude HTML, the Playwright renderer is used instead. PNG bytes are base64-encoded and stored in countries.section_illustrations.<key>.' },
  { label: 'PDF rendering', detail: 'ReportLab composes a 30-60 page PDF: cover, ToC, Executive Brief page, inline charts (PESTLE radar, governance scorecard, revenue forecast, risk matrix, sovereign radar), per-section illustrations (image-compressed to JPEG q70 to stay under the 60s ingress ceiling), references, back page branding. Clickable www.sovereigniq.org footer on all 122+ pages.' },
  { label: 'Budget guard', detail: '_classify_llm_error() is a central helper that detects "Budget has been exceeded" and returns a clean 503 with a "Top up Universal Key" message. Automation aborts immediately on budget breach (no wasted retries).' },
  { label: 'Source injection', detail: 'Each section prompt is appended with dynamic data_sources drawn from 1,000+ curated entries across 20 categories (192 APIs). Keeps citations grounded in the admin-managed research database.' },
];
